import {AiHistoryMessage, Chat, SummaryMessage} from "../types/chat";
import {WidgetConfig} from "../types/config";
import {AgentEvent, ChatType} from "../types/enums";
import ComponentConfig from "../config/componentConfig";
import {waitForSocketConnection} from "./utils/socketUtils";
import {EventFactory} from "./factory/eventFactory";
import AgentChatConnector from "./chat/connector/agentChatConnector";
import AbstractChatConnector from "./chat/connector/abstractChatConnector";
import AbstractWebsocketChat from "./abstractWebsocketChat";
import BotChat from "./botChat";
import {ChatBotSummary} from "./chat/chatBotSummary";
import {MessageFactory} from "./factory/messageFactory";
import AlpineComponent from "./response/alpineComponent";
export default class AgentChat extends AbstractWebsocketChat implements Chat {

    private chatClosedByAgent: boolean = false;
    private isOnLoadHistory: boolean = false;
    private botChat: BotChat|null = null;

    constructor(
        protected config: WidgetConfig,
        protected componentConfig: ComponentConfig,
        protected alpineComponent: AlpineComponent,
    ) {
        super(config, componentConfig, alpineComponent);
    }

    public isAgentChat(): boolean {
        return true;
    }

    public sendClientTypingEvent(): void
    {
        this.getChatConnector().getWebSocket().then(webSocket =>
            waitForSocketConnection(
                webSocket,
                this.getEventHandler().handleSendTypingEvent,
                this.getEventHandler().handleSendEventError,
                EventFactory.createTyping()
            )
        );

        this.updateDisconnectTimer();
    }

    public setAgentIsTyping(duration: number): void
    {
        this.alpineComponent.setAgentIsTyping(duration);
        this.updateDisconnectTimer();
    }

    public async updateDisconnectTimer(): Promise<void>
    {
        let chatConnector = this.getChatConnector();
        if (chatConnector instanceof AgentChatConnector) {
            await chatConnector.updateDisconnectTimer();
        }
    }

    public async setAgentLeft( leftByBot:boolean = false ): Promise<void>
    {
        this.chatClosedByAgent = true;
        await this.getChatConnector().closeConnection();
        this.alpineComponent.closeChatByAgent(leftByBot);
    }

    public hasSomeMessages(): boolean
    {
        return this.getMessageHandler().hasSomeMessages;
    }

    public isChatClosedByAgent(): boolean
    {
        return this.chatClosedByAgent;
    }

    public async loadHistory():Promise<void> {
        this.isOnLoadHistory = true;
        // im AgentenChat erst Bot-History laden, dann die eigene
        if (this.componentConfig.allowChatBot && this.componentConfig.botChatId !== null && this.componentConfig.botChatId !== "") {
            this.botChat = new BotChat(this.config, this.componentConfig, this.alpineComponent);
            document.addEventListener('bot-history-loaded', async () => {
                await this.onBotHistoryLoaded();
            }, {once: true});

            await this.botChat.loadHistoryForCurrentChat();
        }

        if (this.componentConfig.agentChatId === null) {
            return ;
        }

        await this.loadHistoryForCurrentChat();
    }

    public setHistoryLoaded(): void {
        this.isOnLoadHistory = false;
    }

    public async onBotHistoryLoaded(): Promise<void> {
        await this.botChat?.closeWebsocketConnection();
        this.botChat = null;
    }

    public async waitForLoadedHistory(): Promise<void> {
        for (let attempt = 0; attempt < 240; attempt++) {
            if (!this.isOnLoadHistory) {
                return;
            }

            await new Promise(resolve => setTimeout(resolve, 250));
        }
    }

    public async sendSummary(summary: ChatBotSummary): Promise<void>
    {
        let webSocket = await this.getChatConnector().getWebSocket();
        if (webSocket === null) {
            return;
        }

        let messageObject = MessageFactory.createSummary(summary.summary);
        waitForSocketConnection(
            webSocket,
            this.handleSendSummary,
            () => { },
            messageObject
        );
    }

    public handleSendSummary = (
        chatWebSocket: WebSocket|null,
        messageObject: SummaryMessage,
        onError: () => void
    ): void =>
    {
        if (chatWebSocket === null) {
            return;
        }

        chatWebSocket.send(JSON.stringify(messageObject));
    }

    public async sendAiHistory(aiHistoryMessage: AiHistoryMessage): Promise<void>
    {
        let webSocket = await this.getChatConnector().getWebSocket();
        if (webSocket === null) {
            return;
        }

       waitForSocketConnection(
            webSocket,
            this.handleSendAiHistory,
            () => { },
            aiHistoryMessage
        );
    }

    public handleSendAiHistory = (
        chatWebSocket: WebSocket|null,
        messageObject: AiHistoryMessage,
        onError: () => void
    ): void =>
    {
        if (chatWebSocket === null) {
            return;
        }

        chatWebSocket.send(JSON.stringify(messageObject));
    }

    public getChatConnector(): AbstractChatConnector
    {
        if (this.chatConnector === null) {
            this.chatConnector = new AgentChatConnector(
                this.config,
                this.componentConfig,
                this.getWssUrl(),
                this.getMessageHandler(),
                this.getEventHandler(),
                this.alpineComponent
            );
        }

        return this.chatConnector;
    }

    protected getWssUrl(): string {
        return this.config.getWssUrlForAgent();
    }

    protected getChatType(): string {
        return ChatType.AGENT_CHAT;
    }

    protected async sendLeaveEvent(): Promise<void> {
        if(!this.getChatConnector().isWebsocketAvailable()){
            return;
        }

        let webSocket = await this.getChatConnector().getWebSocket();
        this.getEventHandler().handleSendLeaveEvent(webSocket, EventFactory.createLeave());
    }

    protected getSessionId(): null|string {
        return this.componentConfig.getAgentChatId();
    }
}